import { useEffect, useMemo, useState } from 'react';
import { useT, useTX } from '@transifex/react';
import log from 'logging';
import { getTxInstance } from 'utils/tx';
import type { TContext, TFunction } from 'components/translation';

type TranslationsLoader = {
  isReady: boolean;
  t: TFunction;
  tx: TContext;
};

type UseDocumentTranslationsLoaderProps = {
  isGuest: boolean;
  language: string;
};

const DOCUMENT_TRANSLATIONS_SCOPE = 'DOCUMENT_TRANSLATIONS';

export const useDocumentTranslationsLoader = ({
  language,
  isGuest,
}: UseDocumentTranslationsLoaderProps): TranslationsLoader => {
  const defaultInstance = useTX();
  // biome-ignore lint/correctness/useExhaustiveDependencies: The memo needs to be memoized
  const tx = useMemo(() => {
    if (isGuest) {
      return defaultInstance;
    }

    return getTxInstance(DOCUMENT_TRANSLATIONS_SCOPE);
  }, [isGuest]);
  const t = useT(tx);

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    tx.setCurrentLocale(language)
      .then(() => {
        setIsReady(true);
      })
      .catch(() => {
        log.error('Failed to load translations');
        // Fallback to default language if loading fails
        setIsReady(true);
      });
  }, [language, tx]);

  return {
    isReady,
    t,
    tx,
  };
};
